import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
  Cell,
} from 'recharts';
import { IndianRupee, Loader2 } from 'lucide-react';

/**
 * Interface for revenue data point
 */
export interface RevenueData {
  date: string;
  amount: number;
  status?: 'paid' | 'pending' | 'overdue';
}

interface RevenueChartProps {
  data: RevenueData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Custom tooltip component for revenue display
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700">{label}</p>
        <p className="text-amber-600">
          Revenue: <span className="font-bold">₹{payload[0].value?.toLocaleString('en-IN')}</span>
        </p>
        {payload[0].payload.status && (
          <p className="text-sm text-gray-500 capitalize">
            Status: {payload[0].payload.status}
          </p>
        )}
      </div>
    );
  }
  return null;
};

/**
 * Get bar color based on status
 */
const getBarColor = (status?: string): string => {
  switch (status) {
    case 'paid':
      return '#10b981'; // green
    case 'pending':
      return '#f59e0b'; // amber
    case 'overdue':
      return '#ef4444'; // red
    default:
      return '#f59e0b'; // default amber
  }
};

/**
 * RevenueChart - Bar chart showing daily revenue
 *
 * Features:
 * - Displays daily revenue in Indian Rupees (INR)
 * - Gold/amber color scheme for financial data
 * - Color-coded bars based on payment status
 * - Interactive tooltips with formatted currency
 * - Responsive design for all screen sizes
 * - Loading and error states
 *
 * @param data - Array of daily revenue data points
 * @param loading - Loading state indicator
 * @param error - Error message if data fetch failed
 * @param height - Chart height in pixels (default: 350)
 */
const RevenueChart: React.FC<RevenueChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 350,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        <span className="ml-2 text-gray-600">Loading revenue data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <IndianRupee className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No revenue data available</p>
        </div>
      </div>
    );
  }

  // Calculate total revenue
  const totalRevenue = data.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <IndianRupee className="w-5 h-5 mr-2 text-amber-600" />
            Daily Revenue
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Revenue collection tracking
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">Total Revenue</p>
          <p className="text-2xl font-bold text-amber-600">
            ₹{totalRevenue.toLocaleString('en-IN')}
          </p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="date"
            stroke="#666"
            tick={{ fontSize: 12 }}
            tickMargin={10}
          />
          <YAxis
            stroke="#666"
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
            label={{ value: 'Revenue (INR)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="rect"
          />
          <Bar
            dataKey="amount"
            name="Daily Revenue (₹)"
            radius={[8, 8, 0, 0]}
            animationDuration={1000}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.status)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend for status colors */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded mr-1"></div>
          <span className="text-gray-600">Paid</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-amber-500 rounded mr-1"></div>
          <span className="text-gray-600">Pending</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded mr-1"></div>
          <span className="text-gray-600">Overdue</span>
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;
